import { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "./Seo";
import Breadcrumbs, { buildBreadcrumbSchema } from "./Breadcrumbs";
import "./App.css";
import "./PrivacyPolicy.css";
import Footer from "./Footer";
import Navbar from "./Navbar";

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Privacy Policy', path: '/privacy-policy' },
];

const sections = [
  {
    id: "overview",
    badge: "01",
    title: "Overview",
    body: `We, Deveffica Private Limited ("BaatCheet"), recognize that your privacy is very important and take it seriously. This privacy policy ("Privacy Policy") sets out how we collect, use, and disclose your data when you use BaatCheet's mobile application ("App") and the website, 'https://baatcheetapp.in' ("Website"). The Website and the App are collectively referred to as the "Platforms," and individually as "Platform" as the context may require. References to "we," "our," or "us" or the "Company" shall mean the Platforms and/or BaatCheet. Any references to "you," "your," or "user" shall mean any person or entity using our Platforms. We will not use or share your information with anyone except as described in this Privacy Policy.

By using the Platforms, you agree to the terms and conditions of this Privacy Policy. You also consent to our use and disclosure of your Personal Information (as defined below) in the manner described in this Privacy Policy.`,
  },
  {
    id: "info-login",
    badge: "02",
    title: "Log-in Data & Profile Information",
    body: `**Log-in Data**

We collect your name, mobile phone number, gender, and IP address. We may collect an indicative age range which tells us that you are of the appropriate age to access our Platforms and certain features of our Platforms (collectively, "Log-in Data").

We use Log-in Data to:
— Set up and facilitate log-in to a user account on Platforms
— Notify you about changes to the Platforms, including this Privacy Policy
— Facilitate communication, including provision of user support
— Enforce our terms, conditions, and policies and any of our rights, or the rights of our affiliate companies, or other users of the Platforms
— Develop new services and improve the existing services and the Platforms and integrate user feedback and requests
— Provide language and location-based personalization
— Administer the Platforms and for internal operations, including troubleshooting, data analysis, testing, research, security, fraud-detection, account management, and survey purposes
— Better understand how you use and access the Platforms and to improve the user experience on the Platforms
— Verify your gender
— Pseudonymise and aggregate your information for conducting user demographic analysis on items such as region, phone model, operating system platform, system language, and the Platforms version
— Pseudonymise and aggregate your information for collection of web and account traffic statistics of what content and services are used when users access third-party services on the Platforms
— Assess effectiveness of and improve advertising and other marketing and promotional activities

**Additional Profile Information**

Alongside the Log-in Data, we also collect your voice recording, username, birth year, place of birth, time of birth, contents of the communications made with other users by using the Platform and the biography provided by you on your user profile.`,
  },
  {
    id: "content-shared",
    badge: "03",
    title: "Content You Share",
    body: `This comprises all the information you make available to other users via Platforms, comprising such as:

— Information about you or relating to you that is voluntarily shared by you on the Platforms, including without limitation, any quotes, images, political opinions, religious views, marital status, sexual orientation, gender etc.
— Any posts that you make on the Platforms (including your public profile, and photos, videos and voice recordings and photos through your device's camera and/or microphone sensor), any postings from others that you re-post and Location Data and Log Data associated with such postings.

This also includes information about you (including Location Data and Log Data) that other users of the Platforms share about you or any communications they make to you.

We use this information to enforce our terms, conditions, and policies and any of our rights, or the rights of our affiliate companies, or other users of the Platforms; to develop new services and improve the existing services and the Platforms and integrate user feedback and requests; to provide language and location-based personalization; to administer the Platforms and for internal operations, including troubleshooting, data analysis, testing, research, security, fraud-detection, account management, and survey purposes; and to better understand how you use and access the Platforms and to improve the user experience on the Platforms.

**Chat Data**

We also process and analyse your chats with other users as well as the content you publish, as part of the operation of the services, users you connect and interact with, time and date of your exchanges, number of messages you send and receive. This is used to improve your chat suggestion and to improve your future recommendations.

**User Search Data**

Any searches you make on the Platforms are used to provide you quick access to your previous searches and to use analytics for personalization and showing you targeted advertisements.`,
  },
  {
    id: "log-data",
    badge: "04",
    title: "Log Data & Cookies",
    body: `**Log Data**

"Log Data" is information that we automatically collect when you use the Platforms, whether through the use of cookies, web beacons, log files, scripts, including, but not limited to:

— Technical information, such as your mobile carrier-related information, configuration information made available by your web browser or other programs you use to access the Platforms, your IP address and your device's version and identification number
— Information about what you have searched for and looked at while using the Platforms, such as web search terms used, mini applications used, and details of other information and content accessed or requested by you while using the Platforms
— General information about communications on the Platforms, such as the identity of a user that you have communicated with and the time, data and duration of your communications
— Metadata, which means information related to items you have made available through the Platforms, such as the date, time or location that a shared photograph or video was taken or posted

We use Log Data to provide you with a good user experience when you browse our Platforms and also to allow us to improve the Platforms.

**Cookies**

Our Platforms use cookies to distinguish you from other users of our Platforms. We collect the cookie data from the cookies on your device. For detailed information on the cookies we use and the purposes for which we use them see our Cookie Policy section below.

**Surveys**

If you choose to participate in a survey, we may request you to provide certain Personal Information i.e., any information that could be used to identify you ("Personal Information"). We may use a third-party service provider to conduct these surveys and this will be notified to you prior to you completing the survey. We use this information to provide you with a good user experience when you browse our Platforms and also to allow us to improve the Platforms.`,
  },
  {
    id: "location",
    badge: "05",
    title: "Location & Account Security",
    body: `**Location Information**

"Location Data" is information that is derived from your  IP address, and/or public posts that contain location information. You will disclose certain location information to us and to other Platforms users:

— When you use certain location-based features on the Platforms, or mini applications that we may introduce on our Platforms from time to time, and when you share your location with other Platforms users
— When you access the Platforms, as we derive location information from your IP address, device, or internet service to prevent multiple or fraudulent log-ins to your account

We use Location Data for security, fraud-detection and account management (such as to ensure that there are no multiple log-ins or suspicious log-ins on your account); to provide you with location-based services that you choose to use including mini applications that may be made available on the Platforms from time to time (if you choose to disclose your location to any mini application); and to provide language and location customisation.

**Additional Account Security**

We collect your phone number and request access to the SMS on your phone by sending you a One-Time-Password ("OTP"), which you confirm by entering the OTP to confirm your identity, while registering with our Platforms. We use this information to verify your identity and for maintaining your account security.`,
  },
  {
    id: "device-data",
    badge: "06",
    title: "Device Data & Other Information",
    body: `**Device Data**

"Device Data" includes without limitation, the following:

— Device attributes: information such as the operating system, hardware and software versions, signal strength, app and file names and types, and plugins
— Device operations: information about operations and behaviours performed on the device, such as whether a window is foregrounded or backgrounded
— Identifiers: unique identifiers, device IDs, and other identifiers, such as from games, apps or accounts you use
— Device signals: We may collect your Bluetooth signals, and information about nearby Wi-Fi access points, and cell towers
— Network and connections: information such as the name of your mobile operator or ISP, language, time zone, mobile phone number, IP address and connection speed

We use Device Data to facilitate the sharing of any media such as audios, videos, and images using the Platforms; to customize our Platforms to suit your mobile device; to optimize your user experience on our Platforms; to verify your identity so as to enforce our terms, conditions, and policies; and to improve the Platforms.

**Customer Support Information**

Any information that you provide to our customer support team regarding any assistance or support that you may require for using our Platforms from time to time is used to investigate your support issue.

**Contest Information**

Any information that you provide to us in submitting an entry to any contest which may be offered on the Platforms from time to time is used to facilitate your participation in the contest and to award prizes, if applicable.

**Purchase Information**

When you make a purchase directly from us (rather than through platforms such as Android), you provide us or our payment service provider with information, such as your debit or credit card number or other financial information. This is used to process the financial transaction and record the sale.

**Information from Other Sources**

We may also be working closely with third parties (including, for example, business partners, sub-contractors in technical services, analytics providers, search information providers) and may receive information about you from such sources. Such data may be shared internally and combined with data collected on the Platforms.`,
  },
  {
    id: "disclosure",
    badge: "07",
    title: "Disclosure of Your Information",
    body: `We disclose your information in the following manner:

**Content Visible to Others**

Public content i.e., any content you post on your user profile or on another user's profile, such as a post comment, is accessible to everyone, including search engines. Any information you voluntarily disclose for posting to the Platforms, including your profile page information, is accessible to anyone.

When you submit, post or share the content that you choose to make public on the Platforms, it may be re-shared by others. You should consider who you choose to share it with, because people who can see your activity on our Platforms can choose to share it with others on and off our Platforms, including people outside the audience you shared it with. For example, when you share a post or send a message to specific users of our Platforms or accounts, they can download or re-share that content with other users across or off our Platforms. Also, when you comment on someone else's post or like their content, your comment or like is visible to anyone who can see the other person's content.

Users can also use our Platforms to create and share content about you with the audience that they choose, such as posting a photo of you, or tagging you in any of their posts. We reserve the right to share all public content on any social media site or any other online or offline Platforms. **We will never rent or sell your Personal Information to third parties, except on an anonymized basis, unless expressly provided in these terms.**

**Sharing with Our Group Entities**

We may share your Personal Information with any member of our Group. The term "Group" shall mean any entity that is controlled by us, or any entity that is in control of us, or any entity that is under common control with us, whether directly or indirectly.

**What You Share with Others**

When you share content and communicate using our Platforms, you choose the audience for what you share. We do not control and hence shall not be liable for the manner in which such persons (with whom you choose to share content via any sharing options available on the Platforms) use the information that you share with them.`,
  },
  {
    id: "third-parties",
    badge: "08",
    title: "Sharing with Third Parties",
    body: `We may share your information (including Personal Information) with select third parties including:

— Business partners, suppliers and subcontractors ("Affiliates"): Affiliates may use this information to help provide, understand, and improve the Service and Affiliates' own services for the performance of any contract we enter into with you
— Government bodies or law enforcement agencies, if we in good-faith believe that it is reasonably necessary to share your personal data or information in order to comply with any legal obligation or any government request; or to protect the rights or prevent any harm to the property, or safety of the Company, our customers, or public; or to detect, prevent or otherwise address public safety, fraud, security or technical issues
— We may also disclose your information to select third parties if the Company or substantially all of its assets are acquired by a third party, in which case personal data held by it about its customers/users will be one of the transferred assets. If we are involved in a merger, acquisition, bankruptcy, reorganization or sale of assets such that your information would be transferred or become subject to a different Privacy Policy, we will notify you in advance so you can opt out of any such new policy by deleting your account before transfer`,
  },
  {
    id: "premium-services",
    badge: "09",
    title: "Premium Services & In-App Purchases",
    body: `We may make certain products and/or services available to users of the App in consideration of a subscription fee or other fees ("Premium Services"), including the ability to purchase products, services and enhancements, such as virtual goods like wallpaper, frames for your display picture, stickers packages, more chances to participate ("In-App Products").

If you choose to use Premium Services and/or purchase In-App Products, you acknowledge and agree that additional terms may apply to your use of, access to and purchase of such Premium Services and/or In-App Products, and such additional terms are incorporated herein by reference.

You may purchase Premium Services and/or In-App Products through the following payment methods (each, a "Premium Payment Method"): (a) making a purchase through the Google Play or other mobile or web application platforms or storefronts authorised by us (each, a "Third Party Store"), (b) paying with your credit card or debit card, which will be processed by a third-party processor.

Once you have requested a Premium Service or In-App Product, you authorise us to charge your chosen Premium Payment Method and your payment is non-refundable. If payment is not received by us from your chosen Premium Payment Method, you agree to promptly pay all amounts due upon demand by us. If you want to cancel or change your Premium Payment Method at any time, you can do so either via the payment settings option under your profile or by contacting your mobile service provider.

In the event of a conflict between a Third-Party Store's terms and conditions and these terms, the terms and conditions of the Third-Party Store or service provider shall govern and control. We are not responsible and have no liability whatsoever for goods or services you obtain through the Third-Party Store, our third-party service providers or other web sites or web pages.

If you choose to purchase an In-App Product, you will be prompted to enter details for your account with the Third-Party Store you are using (e.g., Android, etc.) ("Mobile Platform Account"), and your Mobile Platform Account will be charged for the Premium Service and/or In-App Product in accordance with the terms disclosed to you at the time of purchase. The pricing may vary due to a number of factors, such as (but not limited to) promotional offers, loyalty bonuses and other discounts that might apply to your age group.

We are not responsible and have no liability whatsoever for any payment processing errors (including card processing, identity verification, analysis and regulatory compliance) or fees or other service-related issues. Further, we do not guarantee that product descriptions or other content and products will be available, accurate, complete, reliable, current or error-free. If we terminate your use of or registration to the App because you have breached these terms, you shall not be entitled to a refund of any unused portion of any fees, payments or other consideration.`,
  },
  {
    id: "security",
    badge: "10",
    title: "Security Practices",
    body: `We have in place appropriate technical and security measures to secure the information collected by us. Where we have given you (or where you have chosen) a username and password which enables you to access the Platforms, you are responsible for keeping these details confidential. We ask you not to share your password with anyone.

**Disclaimer**

Unfortunately, the transmission of information via the internet is not completely secure. Although we will do our best to protect your Personal Information, we cannot guarantee the security of your data transmitted to the Platforms; any transmission is at your own risk. Once we have received your information, we will use strict procedures and security features to try to prevent unauthorized access.`,
  },
  {
    id: "data-storage",
    badge: "11",
    title: "Where We Store Your Personal Information",
    body: `We store your data with Amazon Web Services cloud platform provided by Amazon Web Services, Inc. (headquartered at 410 Terry Ave. N Seattle, Washington 98109, USA) and also on Google Cloud Platform provided by Google LLC on their servers located in India. Both Amazon Web Services and Google Cloud Platform implement security measures to protect the loss, misuse and alteration of the information, details of which are available at <a href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer">https://aws.amazon.com/</a> and <a href="https://cloud.google.com" target="_blank" rel="noopener noreferrer">https://cloud.google.com</a>.

The privacy policies adopted by Amazon Web Services and Google Cloud Platform are available at <a href="https://aws.amazon.com/privacy/?nc1=f_pr" target="_blank" rel="noopener noreferrer">https://aws.amazon.com/privacy/</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>.`,
  },
  {
    id: "your-rights",
    badge: "12",
    title: "Your Rights",
    body: `You are free to remove or delete content from your user account/profile and your account/profile at any point in time. However, the history of your activities and account on our Platforms remains available to us.

You may correct, amend, add or delete Personal Information from your account at any time by logging in and visiting your profile page. You may receive notifications and emails from us on the contact address/es or mobile number registered with us. You may opt out from unwanted e-mail communications from us by following the instructions in the message. However, until your account is deleted you will continue to receive all system e-mails such as non-marketing communications (including but not limited to security alerts, administrative messages and information about changes to our Privacy Policy).

We reserve the right to verify your identity in connection with any requests regarding Personal Information to help ensure that we provide the information we maintain to the individuals to whom it pertains and allow only those individuals or their authorized representatives to exercise rights with respect to that information.

We reserve the right to deny your request if we cannot verify your identity. Where we deny your request in whole or in part, we will endeavour to inform you of the denial and provide an explanation of our actions and the reasons for the denial.

Under Rule 5(6) of the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 ("Rules"), you have a right to ask us to review, correct and amend the information we collect about you at any point in time. Under Rule 5(7) of the Rules, you also have the right to revoke your consent to the collection of your information going forward. However, please note that revoking your consent may negatively affect your use of the Platforms.

You may also ask us not to process your personal data for marketing purposes. You can exercise any of these rights at any time by contacting us at <a href="mailto:baatcheet.connect@gmail.com">baatcheet.connect@gmail.com</a>. However, we require a reasonable period of 30 (thirty) days to comply with any of your requests.`,
  },
  {
    id: "data-retention",
    badge: "13",
    title: "Data Retention",
    body: `We do not retain your Sensitive Personal Information (defined below in this paragraph) for longer than is reasonably required for the purposes for which the information may be lawfully used or as required by applicable law or regulatory requirements for the time being in force. We may also retain your Sensitive Personal Information so long as it is necessary to fulfil the purposes for which it was collected (including for purposes of meeting any legal, administrative, accounting, or other reporting requirements).

For any other content, we will entertain your request for deletion, however, there is a strong possibility that copies of any public content will be retained indefinitely in our systems, including in cached and archived pages of the Platforms, or if other users have copied or saved that information. In addition, due to the nature of the internet, copies of your content, including content that you have removed or deleted from your account, may also exist elsewhere on the internet and be retained indefinitely.

"Sensitive Personal Information" shall mean passwords and any other information that is classified as Sensitive Personal data or Information under Rule 3 of the Rules.`,
  },
  {
    id: "third-party-links",
    badge: "14",
    title: "Third Party Links & Embeds",
    body: `**Third Party Links**

The Platforms may, from time to time, contain links to and from the websites of our partner networks, advertisers, Affiliates and/or any other websites or mobile applications. If you follow a link to any of these websites, please note that these websites have their own privacy policies and that we do not accept any responsibility or liability for these policies. Please check these policies before you submit any personal data to these websites or mobile applications.

**Third-Party Embeds**

Some of the content that you see displayed on the Platforms is not hosted by the Platforms. These "embeds" are hosted by a third-party and embedded in the Platforms. For example: YouTube or Vimeo videos, Imgur or Giphy gifs, SoundCloud audio files, Twitter tweets, or Scribd documents that appear within a post on the Platforms. These files send data to the hosted site just as if you were visiting that site directly.

The Platforms do not control what data third parties collect or what they will do with it. So, third-party embeds on the Platforms are not covered by this Privacy Policy. They are covered by the privacy policy of the third-party service.

Some embeds may ask you for Personal Information, such as your email address, through a form. If you choose to submit your information to a third party this way, we do not know what they may do with it and assume no responsibility or liability in this regard. We suggest that you do not submit Personal Information to any third-party through an embedded form.

If you embed a form that allows submission of Personal Information by users, you must provide near the embedded form a prominent link to an applicable Privacy Policy that clearly states how you intend to use any information collected. Failure to do so may lead the Company to disable the post or take other action to limit or disable your account.`,
  },
  {
    id: "cookie-policy",
    badge: "15",
    title: "Cookie Policy",
    body: `**What are cookies, pixels, and local storage?**

Cookies are small files that the websites place on your computer as you browse the web. Like many websites, we use cookies to discover how people are using our Platforms and to make them work better.

A pixel is a small amount of code on a web page or in an email notification. As many other websites do, we use pixels to learn whether you have interacted with certain web or email content. This helps us measure and improve our Platforms and personalize your experience on our Platforms.

Local storage is an industry-standard technology that allows a website or application to store information locally on your computer or mobile device. We use local storage to customize what we show you based on your past interactions with our Platforms.

**Why do we use these technologies?**

We may use these technologies to show you relevant content, improve your experience and help protect us and our users. We use these to provide you with services on our Platforms that are easier and faster to use, and enable you to easily navigate our Platforms and use its features. We use these to retain authentication information in order to provide certain services to you, to retain your language preference and to provide mapping and location-based services which require your location.

We cannot identify you personally from the information these cookies collect. Only for functionality purposes, personally identifiable information that you disclose like your username and profile picture may be collected. We ensure complete transparency with you about what information is collected, how it is going to be used and with whom it is shared.

**Types of Cookies We Use**

— Necessary Cookies: Essential to make our Platforms work correctly. These cookies do not identify you as an individual.
— Performance Cookies: Enable us to analyse how our Platforms are used that helps us to continually improve its performance. All data is collected and aggregated anonymously.
— Functionality Cookies: Help the Platforms remember the choices you make (such as your language preference, the settings you have applied), store accessibility options, show when you are logged in, and tailor our Platforms for you.
— Targeting / Advertising Cookies: Used to deliver content that is more relevant to you and your interests. They may be used to deliver targeted advertising or to limit the number of times you see an advertisement. They also help us measure the effectiveness of advertising campaigns.

**How can I control Cookies?**

Most internet browsers are initially set up to automatically accept cookies. You can change the settings to block cookies or to alert you when cookies are being sent to your device. If you disable the cookies that we use, this may impact your experience while on the Platforms — for example you may not be able to visit certain areas of our Platforms or you may not receive personalized information when you visit our Platforms.

We will occasionally update this Cookie Policy to reflect changes on our Platforms and services. If we make any material changes in the way we collect, use, or share information held in cookies, we will post these changes to this Cookie Policy. Cookies that have been set in the past — if you have disabled one or more cookies, we may still use information collected from cookies prior to your disabled preference being set. However, we will stop using the disabled cookie to collect any further information.`,
  },
  {
    id: "app-permissions",
    badge: "16",
    title: "App Permissions",
    body: `BaatCheet is a voice and video calling application. To deliver its core functionality, the App requests the following device permissions:

**RECORD_AUDIO — Microphone**

BaatCheet requests access to your device's microphone to enable audio and voice calls. This data type (Audio — Voice or sound recordings) is used solely for App functionality (calls) and is not shared with third parties. This permission is required — the App will not function without it.

**CAMERA**

BaatCheet requests access to your device's camera to enable video calls. This data type (Photos and videos — Videos) is used solely for App functionality (video calls) and is not shared with third parties. This permission is required for video call features.

**POST_NOTIFICATIONS**

BaatCheet requests permission to send notifications to alert you of incoming calls and other time-sensitive communications. No personal data is collected through this permission.

**REQUEST_IGNORE_BATTERY_OPTIMIZATIONS**

BaatCheet is a VoIP/calling app that relies on FCM push notifications to wake the app for incoming calls. Battery optimization restrictions prevent FCM from delivering time-sensitive call notifications. This permission is requested only for partner accounts who need to receive incoming calls reliably. We do not use this permission for any background data collection or processing unrelated to call delivery.

**USE_FULL_SCREEN_INTENT (Android 14+)**

BaatCheet uses the Full Screen Intent permission to display an incoming call UI over the lock screen — this is a standard VoIP use case. This ensures that users do not miss important incoming calls even when their device is locked. This permission is used exclusively for displaying call-related UI and for no other purpose.

All permissions are requested only where necessary for the stated purpose, in accordance with applicable platform policies. You may revoke any permission at any time via your device settings, though doing so may disable certain features of the App.`,
  },
  {
    id: "minors",
    badge: "17",
    title: "Platforms Are Not for Minors",
    body: `The Platforms should only be used or accessed by persons who are competent to enter into a legally binding contract under the Indian Contract Act, 1872. If you are a minor (i.e., you are under the age of 18 years), you may use the Platforms only with the involvement of your parent or guardian.

We do not knowingly collect or use any information from minors. We do not knowingly allow minors to communicate with us or use any of the services on the Platforms. If you are a parent and become aware that your child has provided us with information, please contact us on the website or by e-mailing us at <a href="mailto:baatcheet.connect@gmail.com">baatcheet.connect@gmail.com</a>.`,
  },
  {
    id: "communications",
    badge: "18",
    title: "Communications from Us",
    body: `We may from time to time send you service-related announcements when we consider it necessary to do so (such as when we temporarily suspend the Platforms for maintenance, security, privacy, or administrative-related communications). We send these to you via SMS. You may not opt-out of these service-related announcements, which are not promotional in nature and used solely to protect your account and keep you informed of important changes to the Platforms.`,
  },
  {
    id: "changes",
    badge: "19",
    title: "Changes to This Policy",
    body: `The Company may periodically update this Privacy Policy. Whenever we make any changes to this Privacy Policy that are important for you to know about, we will post the updated Privacy Policy at this link. It is your responsibility to keep checking this page from time to time in order to be aware of any changes made to this Privacy Policy.`,
  },
  {
    id: "grievance",
    badge: "20",
    title: "Grievance Officer",
    body: `In accordance with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, the name and contact details of our Grievance Officer are provided below:

**Name:** Abhishek Jangid

**Address:** 11/547, Patel Nagar, Gali No. 2, Near BSNL Office, Baraut, Bagpat-250611, Uttar Pradesh

**Email:** <a href="mailto:baatcheet.connect@gmail.com">baatcheet.connect@gmail.com</a>

The Grievance Officer shall acknowledge the complaint within 3 (Three) business days and dispose of such complaint within a period of 1 (One) month from the date of its receipt.`,
  },
];

function PrivacyPolicy() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.08 },
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Privacy Policy | Baatcheet"
        description="Read Baatcheet's Privacy Policy to understand how we collect, use, and protect your personal data."
        path="/privacy-policy"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" />
        <i className="cbar s2" />
        <i className="dot s3" />
        <i className="cbar s4" />
        <i className="dot s5" />
        <i className="dot s6" />
        <i className="cbar s7" />
        <i className="dot s8" />
        <i className="cbar s9" />
        <i className="dot s10" />
      </div>

      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>
        {/* ── Hero ── */}
        <section className="pp-hero shell reveal">
          <div className="pp-hero-tag">Legal Documents</div>
          <h1 className="pp-hero-title">
            Privacy
            <br />
            <span className="pp-hero-accent">Policy</span>
          </h1>
          <p className="pp-hero-sub">
            Clear, plain-language policies for every user of BaatCheet. No legal
            jargon maze — just honest answers about how we protect you.
          </p>
          <div className="pp-meta">
            <span className="pp-meta-item">Last updated: May 23, 2026</span>
            <span className="pp-meta-sep">·</span>
            <span className="pp-meta-item">Governed by Indian law</span>
            <span className="pp-meta-sep">·</span>
            <a
              className="pp-meta-item pp-meta-link"
              href="mailto:baatcheet.connect@gmail.com?subject=Privacy%20Query"
            >
              Questions? Email us
            </a>
          </div>
        </section>

        {/* ── Table of Contents ── */}
        <section className="pp-toc-wrap shell reveal">
          <div className="pp-toc">
            <div className="pp-toc-label">Jump to section</div>
            <div className="pp-toc-grid">
              {sections.map((s) => (
                <a key={s.id} className="pp-toc-item" href={`#${s.id}`}>
                  <span className="pp-toc-num">{s.badge}</span>
                  <span className="pp-toc-name">{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── Policy Sections ── */}
        <section className="pp-body shell">
          {sections.map((s, i) => (
            <article
              key={s.id}
              id={s.id}
              className={`pp-article reveal ${i % 2 === 1 ? "pp-article--alt" : ""}`}
            >
              <div className="pp-article-num">{s.badge}</div>
              <div className="pp-article-content">
                <h2 className="pp-article-title">{s.title}</h2>
                <div className="pp-article-body">
                  {s.body.split("\n\n").map((para, pi) => {
                    if (
                      para.startsWith("**") &&
                      para.endsWith("**") &&
                      para.indexOf("**", 2) === para.length - 2
                    ) {
                      return (
                        <h3 key={pi} className="pp-subhead">
                          {para.slice(2, -2)}
                        </h3>
                      );
                    }
                    if (para.startsWith("— ")) {
                      const items = para.split("\n").filter(Boolean);
                      return (
                        <ul key={pi} className="pp-bullets">
                          {items.map((item, ii) => (
                            <li key={ii}>{item.replace(/^— /, "")}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p
                        key={pi}
                        dangerouslySetInnerHTML={{
                          __html: para.replace(
                            /\*\*(.+?)\*\*/g,
                            "<strong>$1</strong>",
                          ),
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* ── CTA strip ── */}
        <section className="pp-cta shell reveal">
          <div className="pp-cta-inner">
            <div className="pp-cta-text">
              <h3>Still have questions?</h3>
              <p>Our team responds within 24 hours — every day of the year.</p>
            </div>
            <a
              className="btn btn-cyan"
              href="mailto:baatcheet.connect@gmail.com?subject=Privacy%20Query"
            >
              ✉ Contact Us
            </a>
          </div>
        </section>

        <div className="pp-related shell">
          <span>Related:</span>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/refund-policy">Refund Policy</Link>
          <Link to="/account-deletion">Account Deletion</Link>
          <Link to="/safety">Safety &amp; Trust</Link>
        </div>
      </main>

      <Footer />
    </>
  );
}

export default PrivacyPolicy;
